import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Plus, Trash2, Pencil, X, Upload, LogOut } from "lucide-react";
import { api, fileUrl, formatApiError } from "@/lib/api";

const CATEGORIES = ["Proposals & Decks", "Publications", "Digital & Social", "Motion"];

const EMPTY_PROJECT = {
  title: "", slug: "", category: "Brand Identity", year: "", client: "", role: "",
  deliverables: "", summary: "", challenge: "", approachText: "", outcome: "",
  cover_image: "", gallery: [], video_url: "", hover_video: "", featured: false, order: 0,
};

const inputCls = "w-full bg-background border border-border px-3 py-2.5 text-sm outline-none focus:border-accent rounded-sm";
const labelCls = "block text-xs uppercase tracking-[0.15em] font-mono text-muted-foreground mb-1.5";

function Field({ label, children }) {
  return (
    <div>
      <span className={labelCls}>{label}</span>
      {children}
    </div>
  );
}

function ImagePicker({ value, onChange, label }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      onChange(data.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };
  return (
    <div>
      <span className={labelCls}>{label}</span>
      <div className="flex items-center gap-3">
        {value ? (
          <img src={fileUrl(value)} alt="" className="w-20 h-14 object-cover border border-border" />
        ) : (
          <div className="w-20 h-14 bg-muted border border-border" />
        )}
        <label className="cursor-pointer inline-flex items-center gap-2 border border-border px-3 py-2 text-xs hover:border-accent hover:text-accent transition-colors">
          <Upload size={13} /> {uploading ? "Uploading…" : "Upload"}
          <input type="file" accept="image/*,video/mp4" className="hidden" onChange={upload} data-testid={`upload-${label.toLowerCase().replace(/\s+/g, "-")}`} />
        </label>
        {value && (
          <button type="button" onClick={() => onChange("")} className="text-muted-foreground hover:text-destructive" aria-label="Remove image">
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

function VideoPicker({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      onChange(data.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };
  return (
    <div>
      <span className={labelCls}>Hover preview video (mp4 / webm — plays on card hover)</span>
      <div className="flex items-center gap-3">
        {value ? (
          <video src={fileUrl(value)} muted loop playsInline className="w-28 h-20 object-cover border border-border" onMouseEnter={(e) => e.target.play()} onMouseLeave={(e) => e.target.pause()} />
        ) : (
          <div className="w-28 h-20 bg-muted border border-border" />
        )}
        <label className="cursor-pointer inline-flex items-center gap-2 border border-border px-3 py-2 text-xs hover:border-accent hover:text-accent transition-colors">
          <Upload size={13} /> {uploading ? "Uploading…" : "Upload"}
          <input type="file" accept="video/mp4,video/webm" className="hidden" onChange={upload} data-testid="upload-hover-video" />
        </label>
        {value && (
          <button type="button" onClick={() => onChange("")} className="text-muted-foreground hover:text-destructive" aria-label="Remove hover video">
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

function Login({ onLogin }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", form);
      onLogin(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-sm border border-border p-10 bg-card">
        <p className="font-serif text-3xl tracking-tight">Studio admin<span className="text-accent">.</span></p>
        <p className="mt-2 text-sm text-muted-foreground">Sign in to manage the portfolio.</p>
        <div className="mt-8 space-y-5">
          <div>
            <span className={labelCls}>Email</span>
            <input type="email" required data-testid="admin-email-input" className={inputCls}
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <span className={labelCls}>Password</span>
            <input type="password" required data-testid="admin-password-input" className={inputCls}
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </div>
          <button type="submit" disabled={loading} data-testid="admin-login-button"
            className="w-full bg-accent text-accent-foreground py-3 text-sm font-medium rounded-sm hover:opacity-90 transition-opacity disabled:opacity-50">
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ProjectEditor({ initial, onClose, onSaved }) {
  const [p, setP] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...p, approach: p.approachText.split("\n").map((s) => s.trim()).filter(Boolean) };
    delete payload.approachText;
    delete payload.next_project;
    try {
      if (p.id) await api.put(`/admin/projects/${p.id}`, payload);
      else await api.post("/admin/projects", payload);
      toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const setGalleryItem = (i, k, v) => {
    const gallery = [...p.gallery];
    gallery[i] = { ...gallery[i], [k]: v };
    set("gallery", gallery);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto" data-testid="project-editor">
      <form onSubmit={save} className="max-w-3xl mx-auto my-10 bg-card border border-border p-8 sm:p-10 space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-2xl">{p.id ? "Edit project" : "New project"}</h2>
          <button type="button" onClick={onClose} data-testid="editor-close-button" aria-label="Close"><X size={20} /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Title *">
            <input required className={inputCls} data-testid="editor-title-input" value={p.title} onChange={(e) => set("title", e.target.value)} />
          </Field>
          <Field label="Slug (auto if blank)">
            <input className={inputCls} data-testid="editor-slug-input" value={p.slug} onChange={(e) => set("slug", e.target.value)} />
          </Field>
          <Field label="Category">
            <select className={inputCls} data-testid="editor-category-select" value={p.category} onChange={(e) => set("category", e.target.value)}>
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Year">
            <input className={inputCls} data-testid="editor-year-input" value={p.year} onChange={(e) => set("year", e.target.value)} />
          </Field>
          <Field label="Client / Firm">
            <input className={inputCls} data-testid="editor-client-input" value={p.client} onChange={(e) => set("client", e.target.value)} />
          </Field>
          <Field label="Role">
            <input className={inputCls} data-testid="editor-role-input" value={p.role} onChange={(e) => set("role", e.target.value)} />
          </Field>
        </div>
        <Field label="Deliverables">
          <input className={inputCls} data-testid="editor-deliverables-input" value={p.deliverables} onChange={(e) => set("deliverables", e.target.value)} />
        </Field>
        <Field label="One-line summary">
          <textarea rows={2} className={inputCls} data-testid="editor-summary-input" value={p.summary} onChange={(e) => set("summary", e.target.value)} />
        </Field>
        <Field label="The challenge">
          <textarea rows={4} className={inputCls} data-testid="editor-challenge-input" value={p.challenge} onChange={(e) => set("challenge", e.target.value)} />
        </Field>
        <Field label="The approach (one decision per line)">
          <textarea rows={4} className={inputCls} data-testid="editor-approach-input" value={p.approachText} onChange={(e) => set("approachText", e.target.value)} />
        </Field>
        <ImagePicker label="Cover image" value={p.cover_image} onChange={(v) => set("cover_image", v)} />
        <Field label="Video URL (YouTube / Vimeo / mp4)">
          <input className={inputCls} data-testid="editor-video-input" value={p.video_url} onChange={(e) => set("video_url", e.target.value)} />
        </Field>
        <VideoPicker value={p.hover_video} onChange={(v) => set("hover_video", v)} />

        <div>
          <span className={labelCls}>Gallery</span>
          <div className="space-y-4">
            {p.gallery.map((g, i) => (
              <div key={i} className="border border-border p-4 space-y-3" data-testid={`gallery-row-${i}`}>
                <div className="flex justify-between items-center">
                  <span className="text-xs font-mono text-muted-foreground">Image {i + 1}</span>
                  <button type="button" onClick={() => set("gallery", p.gallery.filter((_, j) => j !== i))}
                    className="text-muted-foreground hover:text-destructive" aria-label={`Remove gallery image ${i + 1}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
                <ImagePicker label={`Gallery image ${i + 1}`} value={g.src} onChange={(v) => setGalleryItem(i, "src", v)} />
                <div className="grid grid-cols-2 gap-3">
                  <input placeholder="Alt text" className={inputCls} value={g.alt} onChange={(e) => setGalleryItem(i, "alt", e.target.value)} />
                  <select className={inputCls} value={g.layout} onChange={(e) => setGalleryItem(i, "layout", e.target.value)}>
                    <option value="full">Full width</option>
                    <option value="half">Two-up</option>
                  </select>
                </div>
              </div>
            ))}
            <button type="button" data-testid="add-gallery-item-button"
              onClick={() => set("gallery", [...p.gallery, { src: "", alt: "", layout: "half" }])}
              className="inline-flex items-center gap-2 border border-dashed border-border px-4 py-2.5 text-xs hover:border-accent hover:text-accent transition-colors">
              <Plus size={13} /> Add gallery image
            </button>
          </div>
        </div>

        <label className="flex items-center gap-3 text-sm cursor-pointer" data-testid="editor-featured-toggle">
          <input type="checkbox" checked={p.featured} onChange={(e) => set("featured", e.target.checked)} className="w-4 h-4 accent-[#0D5C63]" />
          Feature on home page
        </label>

        <div className="flex gap-3 pt-2">
          <button type="submit" disabled={saving} data-testid="editor-save-button"
            className="bg-accent text-accent-foreground px-8 py-3 text-sm font-medium rounded-sm hover:opacity-90 disabled:opacity-50">
            {saving ? "Saving…" : "Save project"}
          </button>
          <button type="button" onClick={onClose} className="border border-border px-8 py-3 text-sm rounded-sm">Cancel</button>
        </div>
      </form>
    </div>
  );
}

function ProjectsTab() {
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const move = async (i, dir) => {
    const next = [...projects];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setProjects(next);
    await api.post("/admin/projects/reorder", { ids: next.map((p) => p.id) }).catch((e) => toast.error(formatApiError(e)));
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    await api.delete(`/admin/projects/${p.id}`).then(load).catch((e) => toast.error(formatApiError(e)));
    toast.success("Deleted");
  };

  const toggleFeatured = async (p) => {
    const { next_project, ...rest } = p;
    await api.put(`/admin/projects/${p.id}`, { ...rest, featured: !p.featured }).then(load).catch((e) => toast.error(formatApiError(e)));
  };

  const toEditor = (p) => ({
    ...EMPTY_PROJECT, ...p,
    approachText: (p?.approach || []).join("\n"),
    gallery: p?.gallery || [],
  });

  return (
    <div data-testid="admin-projects-tab">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-serif text-2xl">Projects ({projects.length})</h2>
        <button onClick={() => setEditing(toEditor(null))} data-testid="new-project-button"
          className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-5 py-2.5 text-sm rounded-sm hover:opacity-90">
          <Plus size={15} /> New project
        </button>
      </div>
      <div className="border border-border divide-y divide-border">
        {projects.map((p, i) => (
          <div key={p.id} className="flex items-center gap-4 px-4 py-3 bg-card" data-testid={`admin-project-row-${p.slug}`}>
            <div className="flex flex-col">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="disabled:opacity-20" aria-label="Move up" data-testid={`move-up-${p.slug}`}><ArrowUp size={14} /></button>
              <button onClick={() => move(i, 1)} disabled={i === projects.length - 1} className="disabled:opacity-20" aria-label="Move down" data-testid={`move-down-${p.slug}`}><ArrowDown size={14} /></button>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{p.title}</p>
              <p className="text-xs text-muted-foreground">{p.category} · {p.year}</p>
            </div>
            <button onClick={() => toggleFeatured(p)} data-testid={`featured-toggle-${p.slug}`}
              className={`text-xs font-mono uppercase tracking-wide px-3 py-1.5 border rounded-full transition-colors ${
                p.featured ? "border-accent text-accent" : "border-border text-muted-foreground hover:border-accent"
              }`}>
              {p.featured ? "Featured" : "Feature"}
            </button>
            <button onClick={() => setEditing(toEditor(p))} data-testid={`edit-project-${p.slug}`} className="p-2 hover:text-accent" aria-label="Edit"><Pencil size={15} /></button>
            <button onClick={() => remove(p)} data-testid={`delete-project-${p.slug}`} className="p-2 hover:text-destructive" aria-label="Delete"><Trash2 size={15} /></button>
          </div>
        ))}
        {projects.length === 0 && <p className="p-6 text-sm text-muted-foreground">No projects yet.</p>}
      </div>
      {editing && <ProjectEditor initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  const [pw, setPw] = useState({ current: "", next: "" });
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const set = (k, v) => setS((prev) => ({ ...prev, [k]: v }));

  const changePassword = async () => {
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password updated");
      setPw({ current: "", next: "" });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const save = async () => {
    try {
      await api.put("/admin/settings", s);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const uploadPortrait = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      const next = { ...s, portrait_url: data.url };
      await api.put("/admin/settings", next);
      setS(next);
      toast.success("Portrait saved — now showing on Home and About");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removePortrait = async () => {
    try {
      const next = { ...s, portrait_url: "" };
      await api.put("/admin/settings", next);
      setS(next);
      toast.success("Portrait removed");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const uploadResume = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/resume", fd);
      set("resume_url", data.url);
      toast.success("Resume uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="admin-settings-tab">
      <h2 className="font-serif text-2xl">Site settings</h2>
      <Field label="Notification inbox (contact form emails go here)">
        <input type="email" className={inputCls} data-testid="settings-contact-email" value={s.contact_email} onChange={(e) => set("contact_email", e.target.value)} placeholder="you@example.com" />
      </Field>
      <Field label="Public email (shown on site)">
        <input className={inputCls} data-testid="settings-public-email" value={s.public_email} onChange={(e) => set("public_email", e.target.value)} />
      </Field>
      <Field label="LinkedIn URL">
        <input className={inputCls} data-testid="settings-linkedin" value={s.linkedin_url} onChange={(e) => set("linkedin_url", e.target.value)} />
      </Field>
      <Field label="Availability line">
        <input className={inputCls} data-testid="settings-availability" value={s.availability} onChange={(e) => set("availability", e.target.value)} />
      </Field>
      <Field label="Analytics tracking ID (placeholder)">
        <input className={inputCls} data-testid="settings-analytics" value={s.analytics_id} onChange={(e) => set("analytics_id", e.target.value)} placeholder="G-XXXXXXX" />
      </Field>
      <div>
        <span className={labelCls}>Portrait / Banner Image (Home & About) — saves automatically</span>
        <div className="flex items-center gap-3">
          {s.portrait_url ? (
            <img src={fileUrl(s.portrait_url)} alt="Portrait preview" className="w-16 h-20 object-cover border border-border" />
          ) : (
            <div className="w-16 h-20 bg-muted border border-border" />
          )}
          <label className="cursor-pointer inline-flex items-center gap-2 border border-border px-3 py-2 text-xs hover:border-accent hover:text-accent transition-colors">
            <Upload size={13} /> {s.portrait_url ? "Replace" : "Upload"}
            <input type="file" accept="image/*" className="hidden" onChange={uploadPortrait} data-testid="portrait-upload-input" />
          </label>
          {s.portrait_url && (
            <button type="button" onClick={removePortrait} data-testid="portrait-remove-button" className="text-muted-foreground hover:text-destructive" aria-label="Remove portrait">
              <X size={14} />
            </button>
          )}
        </div>
      </div>
      <div>
        <span className={labelCls}>Resume (PDF)</span>
        <div className="flex items-center gap-3">
          <label className="cursor-pointer inline-flex items-center gap-2 border border-border px-4 py-2.5 text-xs hover:border-accent hover:text-accent transition-colors">
            <Upload size={13} /> Upload PDF
            <input type="file" accept="application/pdf" className="hidden" onChange={uploadResume} data-testid="resume-upload-input" />
          </label>
          {s.resume_url && <a href={fileUrl(s.resume_url)} target="_blank" rel="noopener noreferrer" className="text-xs text-accent underline">Current resume</a>}
        </div>
      </div>
      <div>
        <span className={labelCls}>Selected numbers (home page)</span>
        <div className="space-y-3">
          {s.numbers.map((n, i) => (
            <div key={i} className="flex gap-3" data-testid={`number-row-${i}`}>
              <input className={`${inputCls} w-28`} value={n.value} placeholder="10+"
                onChange={(e) => { const numbers = [...s.numbers]; numbers[i] = { ...n, value: e.target.value }; set("numbers", numbers); }} />
              <input className={inputCls} value={n.label} placeholder="Label"
                onChange={(e) => { const numbers = [...s.numbers]; numbers[i] = { ...n, label: e.target.value }; set("numbers", numbers); }} />
              <button type="button" onClick={() => set("numbers", s.numbers.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive" aria-label="Remove number"><X size={15} /></button>
            </div>
          ))}
          <button type="button" onClick={() => set("numbers", [...s.numbers, { value: "", label: "" }])}
            className="inline-flex items-center gap-2 border border-dashed border-border px-4 py-2 text-xs hover:border-accent hover:text-accent" data-testid="add-number-button">
            <Plus size={13} /> Add number
          </button>
        </div>
      </div>
      <div className="border border-border p-5 space-y-4 bg-card">
        <span className={labelCls}>Change admin password</span>
        <input type="password" className={inputCls} placeholder="Current password" data-testid="change-password-current"
          value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} />
        <input type="password" className={inputCls} placeholder="New password (min 10 characters)" data-testid="change-password-new"
          value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} />
        <button onClick={changePassword} data-testid="change-password-button"
          className="border border-foreground/25 px-6 py-2.5 text-sm rounded-sm hover:border-accent hover:text-accent transition-colors">
          Update password
        </button>
      </div>
      <button onClick={save} data-testid="settings-save-button"
        className="bg-accent text-accent-foreground px-8 py-3 text-sm font-medium rounded-sm hover:opacity-90">
        Save settings
      </button>
    </div>
  );
}

function MessagesTab() {
  const [messages, setMessages] = useState([]);
  useEffect(() => { api.get("/admin/messages").then((r) => setMessages(r.data)).catch(() => {}); }, []);
  return (
    <div data-testid="admin-messages-tab">
      <h2 className="font-serif text-2xl mb-6">Messages ({messages.length})</h2>
      <div className="space-y-4">
        {messages.map((m) => (
          <div key={m.id} className="border border-border bg-card p-5" data-testid={`message-${m.id}`}>
            <div className="flex justify-between text-sm">
              <span className="font-medium">{m.name} <span className="text-muted-foreground font-normal">&lt;{m.email}&gt;</span></span>
              <span className="text-xs text-muted-foreground">{new Date(m.created_at).toLocaleDateString()}</span>
            </div>
            <p className="mt-3 text-sm text-foreground/80 whitespace-pre-wrap">{m.message}</p>
          </div>
        ))}
        {messages.length === 0 && <p className="text-sm text-muted-foreground">No messages yet.</p>}
      </div>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("projects");

  useEffect(() => {
    const el = document.createElement("meta");
    el.setAttribute("name", "robots");
    el.setAttribute("content", "noindex, nofollow");
    el.id = "admin-noindex";
    document.head.appendChild(el);
    return () => document.getElementById("admin-noindex")?.remove();
  }, []);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false)).finally(() => setChecking(false));
  }, []);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  if (checking) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground font-mono">Loading…</div>;
  if (!user) return <Login onLogin={setUser} />;

  const tabs = [["projects", "Projects"], ["settings", "Settings"], ["messages", "Messages"]];
  return (
    <div className="min-h-screen bg-muted/30" data-testid="admin-dashboard">
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <span className="font-serif text-xl">Studio admin<span className="text-accent">.</span></span>
          <div className="flex items-center gap-4">
            <a href="/" className="text-sm text-muted-foreground hover:text-accent" data-testid="admin-view-site-link">View site</a>
            <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 text-sm hover:text-destructive">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-6 py-10">
        <nav className="flex gap-2 mb-10">
          {tabs.map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`admin-tab-${key}`}
              className={`px-5 py-2.5 text-sm rounded-full border transition-colors ${
                tab === key ? "bg-accent text-accent-foreground border-accent" : "border-border hover:border-accent"
              }`}>
              {label}
            </button>
          ))}
        </nav>
        {tab === "projects" && <ProjectsTab />}
        {tab === "settings" && <SettingsTab />}
        {tab === "messages" && <MessagesTab />}
      </div>
    </div>
  );
}
